/**
 * Consumption analytics — multi-source comparison.
 *
 * Sources come from /api/analytics/consumption/sources: always 'live'
 * (current strichliste data) plus every uploaded prior-season dataset.
 * The operator picks 1..N sources via the picker; the page fetches
 * each in parallel and renders columns side-by-side.
 *
 * Phase semantics per the backend:
 *   buildup  = Jan 1, year → festival_start
 *   takedown = festival_end → Dec 31, year
 * Year is the source's anchor (current year for live; dataset.year for
 * uploaded). Festival window comes from the live EstimationExclusion or
 * the dataset's own festival_from_at/festival_to_at.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InboxOutlined,
  ReloadOutlined,
  SaveOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import {
  ApiError,
  api,
  type CategoryRow,
  type ConsumptionArticleLifecycle,
  type ConsumptionCategoryRow,
  type ConsumptionDatasetDetail,
  type ConsumptionDatasetMappingRow,
  type ConsumptionDatasetSummary,
  type ConsumptionDayRow,
  type ConsumptionGroupRow,
  type ConsumptionPhaseRow,
  type ConsumptionResponse,
  type ConsumptionSourceListItem,
  type ConsumptionTopArticleRow,
  type ConsumptionWeekRow,
  type ProductGroupRow,
} from "../api/client";

const { Title, Text, Paragraph } = Typography;

const PHASE_COLOR: Record<string, string> = {
  buildup: "blue",
  festival: "gold",
  takedown: "purple",
};

const eur = (cents: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
const fmtInt = (n: number) => new Intl.NumberFormat("de-DE").format(n);

const fmtPhase = (phase: string) => (
  <Tag color={PHASE_COLOR[phase] ?? "default"}>{phase}</Tag>
);

type CsvSection =
  | "per_day"
  | "per_week"
  | "by_category"
  | "by_group"
  | "top_articles";

// ---------------------------------------------------------------------------
// Source-data hook (parallel fetch, one per selected source)
// ---------------------------------------------------------------------------

interface SourceResult {
  source: ConsumptionSourceListItem;
  data: ConsumptionResponse | null;
  loading: boolean;
  error: string | null;
}

function useSourceData(
  selected: ConsumptionSourceListItem[],
  from: Dayjs | null,
  to: Dayjs | null,
  reloadCounter: number,
): SourceResult[] {
  const [results, setResults] = useState<SourceResult[]>([]);
  const key = selected.map((s) => s.source).join(",");

  useEffect(() => {
    let cancelled = false;
    setResults(
      selected.map((s) => ({
        source: s,
        data: null,
        loading: true,
        error: null,
      })),
    );
    Promise.all(
      selected.map(async (s) => {
        const params: Parameters<typeof api.analyticsConsumption>[0] = {};
        if (s.source.startsWith("dataset:"))
          params.dataset_id = Number(s.source.slice("dataset:".length));
        if (from) params.from_date = from.startOf("day").toISOString();
        if (to) params.to_date = to.endOf("day").toISOString();
        try {
          const data = await api.analyticsConsumption(params);
          return {
            source: s,
            data,
            loading: false,
            error: null,
          } as SourceResult;
        } catch (e) {
          return {
            source: s,
            data: null,
            loading: false,
            error: e instanceof ApiError ? e.detail : String(e),
          } as SourceResult;
        }
      }),
    ).then((rs) => {
      if (!cancelled) setResults(rs);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, from, to, reloadCounter]);

  return results;
}

// ---------------------------------------------------------------------------
// Upload modal
// ---------------------------------------------------------------------------

function UploadDatasetModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (id: number) => void;
}) {
  const [form] = Form.useForm<{
    name: string;
    year?: number;
    festival?: [Dayjs | null, Dayjs | null];
  }>();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    form.resetFields();
    setFile(null);
    setBusy(false);
  };

  const submit = async () => {
    const values = await form.validateFields();
    if (!file) {
      message.error("Pick a CSV file first");
      return;
    }
    setBusy(true);
    try {
      const fest = values.festival;
      const result = await api.uploadConsumptionDataset(values.name, file, {
        year: values.year,
        festival_from:
          fest && fest[0] ? fest[0].startOf("day").toISOString() : undefined,
        festival_to:
          fest && fest[1] ? fest[1].endOf("day").toISOString() : undefined,
      });
      message.success(
        `Uploaded ${result.row_count.toLocaleString()} rows · ${result.unmapped_article_count} unmapped`,
      );
      reset();
      onUploaded(result.id);
      onClose();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Upload comparison dataset"
      open={open}
      onCancel={() => {
        reset();
        onClose();
      }}
      onOk={submit}
      okText="Upload"
      okButtonProps={{ loading: busy }}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Dataset name"
          rules={[{ required: true, max: 120 }]}
          extra="e.g. 'Fusion 2025' — shown in the source picker"
        >
          <Input placeholder="Fusion 2025" />
        </Form.Item>
        <Form.Item label="CSV file" required>
          <Upload.Dragger
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={
              file ? [{ uid: "1", name: file.name, status: "done" }] : []
            }
            maxCount={1}
            accept=".csv,.tsv,.sql,.sql.gz,.gz,text/csv,text/tab-separated-values,application/sql,application/gzip"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drop a CSV / TSV / SQL backup
            </p>
            <p className="ant-upload-hint">
              <b>CSV/TSV</b>: <code>created</code>, <code>article_name</code>,{" "}
              <code>amount_cents</code> (required) + <code>quantity</code>,{" "}
              <code>user_id</code> (optional). Delimiter auto-detected.
              <br />
              <b>SQL backup</b>: mysqldump <code>.sql</code> or{" "}
              <code>.sql.gz</code> containing the kiosk <code>article</code> +{" "}
              <code>transactions</code> tables — parsed server-side.
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          name="year"
          label="Year (override)"
          extra="Defaults to the year of the first transaction in the file"
        >
          <InputNumber
            min={2000}
            max={2099}
            placeholder="auto"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item
          name="festival"
          label="Festival window (override)"
          extra="Defaults to the longest tx gap ≥ 48 hours"
        >
          <DatePicker.RangePicker style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mapping editor (modal)
// ---------------------------------------------------------------------------

function MappingEditor({
  open,
  datasetId,
  onClose,
  onSaved,
  categories,
  productGroups,
}: {
  open: boolean;
  datasetId: number | null;
  onClose: () => void;
  onSaved: () => void;
  categories: CategoryRow[];
  productGroups: ProductGroupRow[];
}) {
  const [detail, setDetail] = useState<ConsumptionDatasetDetail | null>(null);
  const [edits, setEdits] = useState<
    Record<
      string,
      { category_id: number | null; product_group_id: number | null }
    >
  >({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || datasetId === null) return;
    setDetail(null);
    setEdits({});
    api.getConsumptionDataset(datasetId).then((d) => {
      setDetail(d);
      const seed: typeof edits = {};
      for (const row of d.mapping) {
        seed[row.article_name] = {
          category_id: row.category_id,
          product_group_id: row.product_group_id,
        };
      }
      setEdits(seed);
    });
  }, [open, datasetId]);

  const setRow = (
    name: string,
    patch: Partial<{
      category_id: number | null;
      product_group_id: number | null;
    }>,
  ) => {
    setEdits((prev) => {
      const cur = prev[name] ?? {
        category_id: null,
        product_group_id: null,
      };
      const next = { ...cur, ...patch };
      if (patch.category_id === null) next.product_group_id = null;
      return { ...prev, [name]: next };
    });
  };

  const save = async () => {
    if (datasetId === null || detail === null) return;
    setBusy(true);
    try {
      const items = Object.entries(edits).map(([article_name, v]) => ({
        article_name,
        category_id: v.category_id,
        product_group_id: v.product_group_id,
      }));
      await api.updateConsumptionDatasetMapping(datasetId, items);
      message.success("Mapping saved");
      onSaved();
      onClose();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groupsByCat = useMemo(() => {
    const m = new Map<number, ProductGroupRow[]>();
    for (const g of productGroups) {
      if (!m.has(g.category_id)) m.set(g.category_id, []);
      m.get(g.category_id)!.push(g);
    }
    return m;
  }, [productGroups]);

  const columns: ColumnsType<ConsumptionDatasetMappingRow> = [
    {
      title: "Article (in CSV)",
      dataIndex: "article_name",
      sorter: (a, b) => a.article_name.localeCompare(b.article_name),
    },
    {
      title: "Units",
      dataIndex: "units",
      align: "right",
      render: fmtInt,
      sorter: (a, b) => a.units - b.units,
      defaultSortOrder: "descend",
    },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
    {
      title: "Category",
      width: 220,
      render: (_, row) => {
        const cur = edits[row.article_name]?.category_id ?? null;
        return (
          <Select
            allowClear
            placeholder="—"
            value={cur ?? undefined}
            onChange={(v) =>
              setRow(row.article_name, { category_id: v ?? null })
            }
            style={{ width: "100%" }}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: "Product group",
      width: 220,
      render: (_, row) => {
        const cur = edits[row.article_name] ?? {
          category_id: null,
          product_group_id: null,
        };
        const opts = cur.category_id
          ? groupsByCat.get(cur.category_id) ?? []
          : [];
        return (
          <Select
            allowClear
            placeholder={cur.category_id ? "—" : "(pick category first)"}
            value={cur.product_group_id ?? undefined}
            onChange={(v) =>
              setRow(row.article_name, { product_group_id: v ?? null })
            }
            disabled={!cur.category_id}
            style={{ width: "100%" }}
            options={opts.map((g) => ({ value: g.id, label: g.name }))}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
  ];

  return (
    <Modal
      title={detail ? `Mapping · ${detail.name}` : "Mapping"}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Save mapping"
      okButtonProps={{ loading: busy, icon: <SaveOutlined /> }}
      width={1100}
    >
      {!detail ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Paragraph type="secondary">
            Each row is one distinct article name in the uploaded CSV. Pick the
            current category + group it belongs to. Unmapped articles roll up
            as <em>(uncategorised)</em> in the analytics.
          </Paragraph>
          <Table
            size="small"
            rowKey="article_name"
            dataSource={detail.mapping}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            scroll={{ y: 480 }}
          />
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Per-source rendering (one column in the side-by-side layout)
// ---------------------------------------------------------------------------

function SourceColumn({
  result,
  onCsvHref,
}: {
  result: SourceResult;
  onCsvHref: (section: CsvSection) => string;
}) {
  if (result.loading) {
    return (
      <Card size="small" title={result.source.label}>
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      </Card>
    );
  }
  if (result.error) {
    return (
      <Card size="small" title={result.source.label}>
        <Alert type="error" message={result.error} />
      </Card>
    );
  }
  const d = result.data;
  if (!d) return null;

  const phaseCols: ColumnsType<ConsumptionPhaseRow> = [
    { title: "Phase", dataIndex: "phase", render: fmtPhase },
    { title: "Taps", dataIndex: "taps", align: "right", render: fmtInt },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const dayCols: ColumnsType<ConsumptionDayRow> = [
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: "Phase",
      dataIndex: "phase",
      render: fmtPhase,
      filters: [
        { text: "buildup", value: "buildup" },
        { text: "festival", value: "festival" },
        { text: "takedown", value: "takedown" },
      ],
      onFilter: (val, row) => row.phase === val,
    },
    { title: "Taps", dataIndex: "taps", align: "right", render: fmtInt },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const weekCols: ColumnsType<ConsumptionWeekRow> = [
    { title: "ISO Week", dataIndex: "iso_week" },
    { title: "Taps", dataIndex: "taps", align: "right", render: fmtInt },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const catCols: ColumnsType<ConsumptionCategoryRow> = [
    { title: "Phase", dataIndex: "phase", render: fmtPhase },
    { title: "Category", dataIndex: "category" },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const groupCols: ColumnsType<ConsumptionGroupRow> = [
    { title: "Phase", dataIndex: "phase", render: fmtPhase },
    { title: "Category", dataIndex: "category" },
    {
      title: "Group",
      dataIndex: "product_group",
      render: (g: string | null) => g ?? <Text type="secondary">—</Text>,
    },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const topCols: ColumnsType<ConsumptionTopArticleRow> = [
    { title: "Name", dataIndex: "name" },
    {
      title: "Category",
      dataIndex: "category",
      render: (c: string | null) => c ?? <Text type="secondary">—</Text>,
    },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: eur,
    },
  ];

  const lifecycleCols: ColumnsType<ConsumptionArticleLifecycle> = [
    { title: "Article", dataIndex: "name" },
    {
      title: "First seen",
      dataIndex: "first_seen",
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Last seen",
      dataIndex: "last_seen",
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm"),
    },
    { title: "Units", dataIndex: "units", align: "right", render: fmtInt },
  ];

  const csvBtn = (section: CsvSection) => (
    <Button
      size="small"
      icon={<DownloadOutlined />}
      href={onCsvHref(section)}
    >
      CSV
    </Button>
  );

  return (
    <Card
      size="small"
      title={
        <Space>
          <strong>{d.source_label}</strong>
          <Text type="secondary">
            ({dayjs(d.scope_from).format("MMM D, YYYY")}—
            {dayjs(d.scope_to).format("MMM D, YYYY")})
          </Text>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col>
          <Statistic title="Taps" value={d.totals.taps} />
        </Col>
        <Col>
          <Statistic title="Units" value={d.totals.units} />
        </Col>
        <Col>
          <Statistic
            title="Revenue"
            value={d.totals.revenue_cents / 100}
            precision={2}
            suffix="€"
          />
        </Col>
        <Col>
          <Statistic
            title="Excluded non-article"
            value={d.excluded_non_article_tx}
          />
        </Col>
      </Row>

      <Space size={4} wrap style={{ marginBottom: 12 }}>
        {d.phases.map((p) => (
          <Tag
            key={`${p.name}-${p.from_at}`}
            color={PHASE_COLOR[p.name] ?? "default"}
          >
            {p.name}: {dayjs(p.from_at).format("MMM D")}—
            {dayjs(p.to_at).format("MMM D")}
          </Tag>
        ))}
      </Space>

      <Tabs
        size="small"
        defaultActiveKey="phase"
        items={[
          {
            key: "phase",
            label: "Phases",
            children: (
              <Table
                size="small"
                rowKey="phase"
                dataSource={d.per_phase}
                columns={phaseCols}
                pagination={false}
              />
            ),
          },
          {
            key: "day",
            label: `Per day (${d.per_day.length})`,
            children: (
              <>
                <div style={{ marginBottom: 8 }}>{csvBtn("per_day")}</div>
                <Table
                  size="small"
                  rowKey="date"
                  dataSource={d.per_day}
                  columns={dayCols}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  scroll={{ y: 420 }}
                />
              </>
            ),
          },
          {
            key: "week",
            label: `Per week (${d.per_week.length})`,
            children: (
              <>
                <div style={{ marginBottom: 8 }}>{csvBtn("per_week")}</div>
                <Table
                  size="small"
                  rowKey="iso_week"
                  dataSource={d.per_week}
                  columns={weekCols}
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: "cat",
            label: "Category",
            children: (
              <>
                <div style={{ marginBottom: 8 }}>{csvBtn("by_category")}</div>
                <Table
                  size="small"
                  rowKey={(r) =>
                    `${r.phase}-${r.category_id ?? "uncat"}`
                  }
                  dataSource={d.by_category}
                  columns={catCols}
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: "group",
            label: "Group",
            children: (
              <>
                <div style={{ marginBottom: 8 }}>{csvBtn("by_group")}</div>
                <Table
                  size="small"
                  rowKey={(r) =>
                    `${r.phase}-${r.category_id ?? "u"}-${r.product_group_id ?? "u"}`
                  }
                  dataSource={d.by_group}
                  columns={groupCols}
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: "top",
            label: "Top 20",
            children: (
              <>
                <div style={{ marginBottom: 8 }}>{csvBtn("top_articles")}</div>
                <Table
                  size="small"
                  rowKey="name"
                  dataSource={d.top_articles}
                  columns={topCols}
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: "outliers",
            label: "Outliers",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <div>
                  <Title level={5}>Daily closer (last sale of the day)</Title>
                  <Table
                    size="small"
                    rowKey="date"
                    dataSource={d.outliers.daily_closers}
                    columns={[
                      { title: "Date", dataIndex: "date" },
                      {
                        title: "Time",
                        dataIndex: "ts",
                        render: (v: string) => dayjs(v).format("HH:mm"),
                      },
                      { title: "Article", dataIndex: "article_name" },
                    ]}
                    pagination={{ pageSize: 12 }}
                    scroll={{ y: 320 }}
                  />
                </div>
                <div>
                  <Title level={5}>Peak hour per day</Title>
                  <Table
                    size="small"
                    rowKey="date"
                    dataSource={d.outliers.peak_hours}
                    columns={[
                      { title: "Date", dataIndex: "date" },
                      {
                        title: "Hour (UTC)",
                        dataIndex: "hour",
                        render: (h: number) =>
                          `${h.toString().padStart(2, "0")}:00`,
                      },
                      {
                        title: "Taps",
                        dataIndex: "taps",
                        align: "right",
                        render: fmtInt,
                      },
                    ]}
                    pagination={{ pageSize: 12 }}
                    scroll={{ y: 320 }}
                  />
                </div>
                <div>
                  <Title level={5}>
                    Vanished mid-period ({d.outliers.vanished_articles.length})
                  </Title>
                  <Paragraph type="secondary">
                    Sold before the scope midpoint, then never seen again.
                  </Paragraph>
                  <Table
                    size="small"
                    rowKey="name"
                    dataSource={d.outliers.vanished_articles}
                    columns={lifecycleCols}
                    pagination={false}
                  />
                </div>
                <div>
                  <Title level={5}>
                    New mid-period ({d.outliers.new_articles.length})
                  </Title>
                  <Paragraph type="secondary">
                    First sale was after the scope midpoint.
                  </Paragraph>
                  <Table
                    size="small"
                    rowKey="name"
                    dataSource={d.outliers.new_articles}
                    columns={lifecycleCols}
                    pagination={false}
                  />
                </div>
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const ConsumptionPage = () => {
  const [sources, setSources] = useState<ConsumptionSourceListItem[]>([]);
  const [selected, setSelected] = useState<string[]>(["live"]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const [datasets, setDatasets] = useState<ConsumptionDatasetSummary[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mappingId, setMappingId] = useState<number | null>(null);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroupRow[]>([]);

  const refreshSources = useCallback(async () => {
    const [s, d, c, g] = await Promise.all([
      api.consumptionSources(),
      api.consumptionDatasets(),
      api.listCategories(),
      api.listProductGroups(),
    ]);
    setSources(s);
    setDatasets(d);
    setCategories(c);
    setProductGroups(g);
  }, []);

  useEffect(() => {
    refreshSources();
  }, [refreshSources]);

  const selectedSources = useMemo(
    () => sources.filter((s) => selected.includes(s.source)),
    [sources, selected],
  );

  const results = useSourceData(
    selectedSources,
    fromDate,
    toDate,
    reloadCounter,
  );

  const csvHrefForSource =
    (source: ConsumptionSourceListItem) => (section: CsvSection) => {
      const params: Parameters<typeof api.consumptionCsvUrl>[1] = {};
      if (source.source.startsWith("dataset:"))
        params.dataset_id = Number(source.source.slice("dataset:".length));
      if (fromDate) params.from_date = fromDate.startOf("day").toISOString();
      if (toDate) params.to_date = toDate.endOf("day").toISOString();
      return api.consumptionCsvUrl(section, params);
    };

  const datasetCols: ColumnsType<ConsumptionDatasetSummary> = [
    { title: "Name", dataIndex: "name" },
    { title: "Year", dataIndex: "year", align: "right" },
    {
      title: "Scope",
      render: (_, r) =>
        `${dayjs(r.scope_from).format("MMM D")}—${dayjs(r.scope_to).format("MMM D, YYYY")}`,
    },
    {
      title: "Festival",
      render: (_, r) =>
        r.festival_from_at && r.festival_to_at
          ? `${dayjs(r.festival_from_at).format("MMM D")}—${dayjs(r.festival_to_at).format("MMM D")}`
          : "—",
    },
    {
      title: "Rows",
      dataIndex: "row_count",
      align: "right",
      render: fmtInt,
    },
    {
      title: "Mapping",
      render: (_, r) =>
        r.unmapped_article_count === 0 ? (
          <Tag color="green">all mapped</Tag>
        ) : (
          <Tooltip
            title={`${r.unmapped_article_count} article names roll up as (uncategorised)`}
          >
            <Tag color="orange" icon={<WarningOutlined />}>
              {r.unmapped_article_count} unmapped
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: "Uploaded",
      dataIndex: "uploaded_at",
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "",
      width: 180,
      render: (_, r) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setMappingId(r.id)}
          >
            Mapping
          </Button>
          <Popconfirm
            title="Delete this dataset?"
            onConfirm={async () => {
              await api.deleteConsumptionDataset(r.id);
              await refreshSources();
              setSelected((s) =>
                s.filter((src) => src !== `dataset:${r.id}`),
              );
            }}
          >
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Consumption</Title>
      <Paragraph type="secondary" style={{ maxWidth: 760 }}>
        Kiosk sales rolled up by festival phase. Compare current numbers
        against uploaded prior-season CSVs side-by-side. Phases:{" "}
        <b>buildup</b> (Jan 1 → festival start), <b>festival</b>, and{" "}
        <b>takedown</b> (festival end → Dec 31). Festival comes from the{" "}
        <a href="/settings/estimation-exclusions">festival exclusion</a> for
        live and from the dataset row for uploaded sources.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap align="end">
          <div style={{ minWidth: 320 }}>
            <Text type="secondary" style={{ display: "block" }}>
              Sources
            </Text>
            <Select
              mode="multiple"
              value={selected}
              onChange={setSelected}
              options={sources.map((s) => ({
                value: s.source,
                label: `${s.label}${s.year ? ` (${s.year})` : ""}`,
              }))}
              style={{ minWidth: 320 }}
              placeholder="Pick one or more sources"
            />
          </div>
          <Space direction="vertical" size={2}>
            <Text type="secondary">From (override)</Text>
            <DatePicker value={fromDate} onChange={setFromDate} allowClear />
          </Space>
          <Space direction="vertical" size={2}>
            <Text type="secondary">To (override)</Text>
            <DatePicker value={toDate} onChange={setToDate} allowClear />
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setReloadCounter((n) => n + 1)}
            type="primary"
          >
            Run
          </Button>
          <Divider type="vertical" />
          <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
            Upload CSV
          </Button>
        </Space>
      </Card>

      {results.length === 0 ? (
        <Empty
          description="Pick at least one source"
          style={{ marginTop: 80 }}
        />
      ) : results.length === 1 ? (
        <SourceColumn
          result={results[0]}
          onCsvHref={csvHrefForSource(results[0].source)}
        />
      ) : (
        <Row gutter={16}>
          {results.map((r) => (
            <Col
              key={r.source.source}
              xs={24}
              xl={results.length <= 2 ? 12 : 8}
            >
              <SourceColumn
                result={r}
                onCsvHref={csvHrefForSource(r.source)}
              />
            </Col>
          ))}
        </Row>
      )}

      <Card
        size="small"
        title="Datasets"
        style={{ marginTop: 24 }}
        extra={
          <Button
            icon={<UploadOutlined />}
            onClick={() => setUploadOpen(true)}
            size="small"
          >
            Upload
          </Button>
        }
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={datasets}
          columns={datasetCols}
          pagination={false}
          locale={{
            emptyText:
              "No datasets uploaded yet — upload a prior season's CSV",
          }}
        />
      </Card>

      <UploadDatasetModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={async (id) => {
          await refreshSources();
          setSelected((s) =>
            s.includes(`dataset:${id}`) ? s : [...s, `dataset:${id}`],
          );
          setMappingId(id);
        }}
      />
      <MappingEditor
        open={mappingId !== null}
        datasetId={mappingId}
        onClose={() => setMappingId(null)}
        onSaved={async () => {
          await refreshSources();
          setReloadCounter((n) => n + 1);
        }}
        categories={categories}
        productGroups={productGroups}
      />
    </div>
  );
};
